import {
  JSX,
  createMemo,
  createSignal,
  createEffect,
  on,
  onCleanup,
  onMount,
} from 'solid-js';
import { t } from '@trimble/i18n';
import { ControlDropdown } from '../control-dropdown/ControlDropdown';
import { ControlMenuItemType } from '../control-menu';

export interface UvCameraControlSelectorProps {
  onCameraSelected(
    mode: CameraMode,
    e: Event
  ): boolean | Promise<boolean> | undefined | Promise<undefined>;
}

type CameraMode =
  | 'rotate'
  | 'pan'
  | 'lookAround'
  | 'pathNavigation'
  | 'profileView';

interface CameraModeConfig {
  id: CameraMode;
  label: string;
  icon: string;
  labelRight: string;
}

export function UvCameraControlSelector(
  props: UvCameraControlSelectorProps
): JSX.Element {
  const [currentCameraMode, setCurrentCameraMode] =
    createSignal<string>('rotate');

  const cameraModes = createMemo((): CameraModeConfig[] => [
    {
      id: 'rotate',
      label: t('general.camera.rotate'),
      icon: 'tc-icon-orbit',
      labelRight: 'CTRL+U',
    },
    {
      id: 'pan',
      label: t('general.camera.pan'),
      icon: 'tc-icon-hand',
      labelRight: 'CTRL+I',
    },
    {
      id: 'lookAround',
      label: t('general.camera.lookAround'),
      icon: 'tc-icon-turn-head',
      labelRight: 'CTRL+P',
    },
    {
      id: 'pathNavigation',
      label: t('general.camera.pathNavigation'),
      icon: 'tc-icon-path-navigatio',
      labelRight: 'CTRL+O',
    },
    {
      id: 'profileView',
      label: t('general.camera.profileView'),
      icon: 'tc-icon-image',
      labelRight: 'CTRL+M',
    },
  ]);

  createEffect(
    on(
      currentCameraMode,
      (newMode, previousMode) => {
        const mode = newMode as CameraMode;
        const syntheticEvent = new CustomEvent('camera-mode-change', {
          detail: { mode },
        });
        void (async (): Promise<void> => {
          const result = await props.onCameraSelected(mode, syntheticEvent);
          if (result === false && previousMode) {
            setCurrentCameraMode(previousMode);
          }
        })();
      },
      { defer: true }
    )
  );

  const items = createMemo((): ControlMenuItemType[] => [
    {
      id: 'camera-mode',
      type: 'radiogroup',
      value: currentCameraMode,
      setValue: setCurrentCameraMode,
      options: cameraModes().map((mode) => ({
        id: mode.id,
        label: mode.label,
        icon: mode.icon,
        labelRight: mode.labelRight,
      })),
    },
  ]);

  const currentIcon = createMemo(
    () => cameraModes().find((m) => m.id === currentCameraMode())?.icon ?? ''
  );

  const handleKeyboardShortcut = (e: KeyboardEvent): void => {
    if (!e.ctrlKey) return;
    const keyToCameraModeMap: Partial<Record<string, CameraMode>> = {
      ['KeyU']: 'rotate',
      ['KeyI']: 'pan',
      ['KeyP']: 'lookAround',
      ['KeyO']: 'pathNavigation',
      ['KeyM']: 'profileView',
    };
    const mode = keyToCameraModeMap[e.code];
    if (mode) {
      e.preventDefault();
      setCurrentCameraMode(mode);
    }
  };

  onMount(() => {
    document.addEventListener('keydown', handleKeyboardShortcut);
  });
  onCleanup(() => {
    document.removeEventListener('keydown', handleKeyboardShortcut);
  });

  return (
    <div data-testid="camera-control-selector">
      <ControlDropdown anchorIcon={currentIcon()} items={items()} />
    </div>
  );
}
